import { Component, computed, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { CheckboxComponent } from '../checkbox/checkbox.component';
import { TreeComponent, TreeNodeDirective, type TreeItem } from '../tree';

@Component({
  selector: 'sb-checkbox-tree',
  templateUrl: './checkbox-tree.component.html',
  imports: [TreeComponent, CheckboxComponent, TreeNodeDirective, TranslatePipe],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxTreeComponent), multi: true }],
})
export class CheckboxTreeComponent<T = CheckboxTreeItemValue> implements ControlValueAccessor {
  items = input.required<CheckboxTreeItem<T>[]>();
  canSelectAll = input(false);

  disabled = model(false);
  value = model<T[] | null>();

  extendedItems = computed(() => this.buildExtendedItems(this.items(), this.value()));
  allItemsAreSelected = computed(
    () => !!this.value()?.length && this.value()?.length === this.selectableItemsValues().length,
  );

  private selectableItemsValues = computed(() =>
    this.items().flatMap((item) =>
      item.children && item.children.length ? item.children.map((child) => child.value) : [item.value],
    ),
  );

  private onChange: ((value: T[] | null) => void) | undefined;
  private onTouched: (() => void) | undefined;

  setDisabledState(isDisabled: boolean) {
    this.disabled.set(isDisabled);
  }

  registerOnChange(fn: (value: T[] | null) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  writeValue(value: T[] | null | undefined) {
    this.value.set(value);
  }

  onCheckboxChange(checked: boolean, item: CheckboxTreeExtendedItem<T>) {
    const currentSelectedItems = this.value() || [];

    const itemValues = this.getAllItemValues(item);

    const newSelectedItems = checked
      ? Array.from(new Set([...currentSelectedItems, ...itemValues]))
      : currentSelectedItems.filter((val) => !itemValues.includes(val));

    this.setSelectedItems(newSelectedItems);
  }

  onSelectAllToggle() {
    const newValue = this.allItemsAreSelected() ? [] : this.selectableItemsValues();

    this.setSelectedItems(newValue);
  }

  /**
   * A workaround for ng-template context typing limitations
   */
  typecastTreeItem(item: unknown): CheckboxTreeExtendedItem<T> {
    return item as CheckboxTreeExtendedItem<T>;
  }

  private setSelectedItems(value: T[]) {
    this.value.set(value);
    this.onTouched?.();
    this.onChange?.(value);
  }

  /**
   * Builds extended items with checked and indeterminate states
   */
  private buildExtendedItems(
    items: CheckboxTreeItem<T>[],
    selectedItems: T[] | null | undefined,
  ): CheckboxTreeExtendedItem<T>[] {
    const selectedItemsLookup = selectedItems ? new Set(selectedItems) : undefined;

    return items.map((item) => {
      const children = item.children ? this.buildExtendedItems(item.children, selectedItems) : [];

      const allChildrenChecked = children.length > 0 && children.every((child) => child.checked);
      const someChildrenChecked = children.some((child) => child.checked || child.indeterminate);

      const checked = selectedItemsLookup?.has(item.value) || allChildrenChecked;
      const indeterminate = !checked && someChildrenChecked;

      return { ...item, children, checked, indeterminate };
    });
  }

  private getAllItemValues(item: CheckboxTreeItem<T>): T[] {
    return item.children?.length ? item.children.flatMap((child) => this.getAllItemValues(child)) : [item.value];
  }
}

export type CheckboxTreeItemValue = string | number;

export interface CheckboxTreeItem<T = CheckboxTreeItemValue> extends TreeItem<T> {
  disabled?: boolean;
  children?: CheckboxTreeItem<T>[];
}

interface CheckboxTreeExtendedItem<T = CheckboxTreeItemValue> extends CheckboxTreeItem<T> {
  checked: boolean;
  indeterminate: boolean;
  children?: CheckboxTreeExtendedItem<T>[];
}
